import React, { useContext } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { TranslatorContext } from '@jutro/locale';
import { Button, Icon } from '@jutro/components';
import { ErrorLevel } from 'gw-portals-edge-validation-js';
import { WizardContext } from 'gw-portals-wizard-react';
import { SG_AA_PREMIUM_PAGE_ACTION } from 'sg-adobe-analytics/AdobeAnalyticsConstants';
import SgQnBLobSpecificUtil from 'sg-qnb-lob-specific-util';
import { SgSessionService } from 'saga-modules';
import styles from './SgBreadcrumbs.module.scss';

/**
 * @typedef {import('gw-portals-edge-validation-js/errors').GenericUIIssue} GenericUIIssue
 */

/**
 * Renders an icon for steps with errors
 * @param {Array<GenericUIIssue>} errorsAndWarnings array
 * @returns {Object}
 */
function renderErrors(errorsAndWarnings = []) {
    const highestLevel = errorsAndWarnings
        .map((err) => err.level)
        .reduce((currentHighest, currentError) => {
            if (!currentHighest) {
                return currentError;
            }
            if (currentHighest === ErrorLevel.LEVEL_WARN) {
                // currentError can only Greater or Equal than currentHighest
                return currentError;
            }
            return currentHighest;
        }, null);
    switch (highestLevel) {
        case ErrorLevel.LEVEL_ERROR:
            return <Icon icon="mi-remove_circle_outline" className={styles.errorIssue} />;
        case ErrorLevel.LEVEL_WARN:
            return <Icon icon="mi-warning" className={styles.warningIssue} />;
        default:
            return null;
    }
}

function jumpToStep(index, jumpTo, wizardContext) {
    const { wizardData } = wizardContext;
    if (wizardData && !SgSessionService.handleSession(wizardData.value)) {
        return;
    }
    jumpTo(index);
}

function renderSteps(
    translator,
    steps,
    currentStepIndex,
    stepsWithErrors,
    jumpTo,
    wizardContext,
    customMessageModal,
    disableAllBreadcrumbs
) {
    let breadCrumbIndex = 0;
    return steps.map((step, index) => {
        const liClassName = classNames(styles.step, {
            [styles.active]: index === currentStepIndex,
            [styles.notVisited]: currentStepIndex < index,
            [styles.visited]: index < currentStepIndex,
        });
        if (!step.stepProps.hideBreadcrumb) {
            breadCrumbIndex += 1;
        }
        return (
            (!step.stepProps.hideBreadcrumb
                ? (
                    <li className={liClassName} key={step.id}>
                        <Button
                            id={`breadcrumbLink_${translator(step.title)}`}
                            className={styles.navigationButton}
                            onClick={() => jumpToStep(
                                index,
                                jumpTo,
                                wizardContext
                            )
                            }
                            disabled={currentStepIndex < index || disableAllBreadcrumbs}
                            adobeValue={
                                `${SG_AA_PREMIUM_PAGE_ACTION.LEVEL_1.BREADCRUMBS}|${SG_AA_PREMIUM_PAGE_ACTION.LEVEL_2.TEXT}|${translator(step.title)
                                    .toLowerCase()}`
                            }
                        >
                            {(breadCrumbIndex).toString()}
                            {renderErrors(stepsWithErrors[step.id])}
                        </Button>
                        <br />
                        <span className={styles.stepNameLabel}>{translator(step.title)}</span>
                    </li>
                ) : null)

        );
    });
}

function renderBreadcrumbs(wizardContext, translator, customMessageModal, disableBreadcrumbs) {
    const {
        steps, currentStepIndex, jumpTo, stepsWithErrors
    } = wizardContext;
    const disableAllBreadcrumbs = steps[currentStepIndex].stepProps.disableAllBreadcrumbs || disableBreadcrumbs;
    return (
        <div className={styles.breadcrumbWrapper}>
            <nav aria-label="Breadcrumbs">
                <ul className={styles.stepsNav}>
                    {renderSteps(
                        translator,
                        steps,
                        currentStepIndex,
                        stepsWithErrors,
                        jumpTo,
                        wizardContext,
                        customMessageModal,
                        disableAllBreadcrumbs
                    )}
                </ul>
            </nav>
        </div>
    );
}

function renderReadOnlySteps(steps, translator, activeBreadcrumb) {
    const defaultQuoteStepIndex = SgQnBLobSpecificUtil.getQuotePageStepIndex();
    const quoteStepIndex = activeBreadcrumb === undefined ? defaultQuoteStepIndex : activeBreadcrumb;
    return steps.map((step, index) => {
        const liClassName = classNames(styles.step, {
            [styles.active]: index === quoteStepIndex,
            [styles.notVisited]: index > quoteStepIndex,
            [styles.visited]: index <= quoteStepIndex,
        });
        return (
            (
                <li className={liClassName} key={translator(step)}>
                    <Button
                        className={styles.navigationButton}
                        disabled
                    >
                        {quoteStepIndex > -1 && (index + 1).toString()}
                    </Button>
                    <br />
                    <span className={styles.stepNameLabel}>{translator(step)}</span>
                </li>
            )
        );
    });
}

function renderReadOnlyBreadcrumbs(steps, translator, activeBreadcrumb) {
    return (
        <div className={styles.breadcrumbWrapper}>
            <nav aria-label="Breadcrumbs">
                <ul className={styles.stepsNav}>
                    {renderReadOnlySteps(
                        steps,
                        translator,
                        activeBreadcrumb
                    )}
                </ul>
            </nav>
        </div>
    );
}

function SgBreadcrumbs(props) {
    const {
        customMessageModal,
        readOnlySteps,
        disableBreadcrumbs,
        activeBreadcrumb
    } = props;
    const wizardContext = useContext(WizardContext);
    const translator = useContext(TranslatorContext);
    return readOnlySteps
        ? renderReadOnlyBreadcrumbs(readOnlySteps, translator, activeBreadcrumb)
        : renderBreadcrumbs(wizardContext, translator, customMessageModal, disableBreadcrumbs);
}

SgBreadcrumbs.propTypes = {
    customMessageModal: PropTypes.shape({})
};
SgBreadcrumbs.defaultProps = {
    customMessageModal: {}
};
export default SgBreadcrumbs;
